import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import knowledgeService from "../services/knowledgeService";

export const useKnowledge = (agentId) => {
  const queryClient = useQueryClient();

  // Get knowledge list
  const {
    data: knowledgeData,
    isLoading: isLoadingKnowledge,
    error: knowledgeError,
    refetch: refetchKnowledge,
  } = useQuery({
    queryKey: ["knowledge", agentId],
    queryFn: () => knowledgeService.getKnowledge(agentId),
    enabled: !!agentId,
    staleTime: 30000, // Consider data stale after 30 seconds
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Add manual knowledge mutation
  const addManualKnowledgeMutation = useMutation({
    mutationFn: (data) => knowledgeService.addManualKnowledge(agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["knowledge", agentId]);
    },
  });

  // Update manual knowledge mutation
  const updateManualKnowledgeMutation = useMutation({
    mutationFn: (data) =>
      knowledgeService.updateManualKnowledge(agentId, data.id, {
        title: data.title,
        content: data.content,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["knowledge", agentId]);
      toast.success("Knowledge updated successfully!");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update knowledge"
      );
    },
  });

  // Upload document mutation
  const uploadDocumentMutation = useMutation({
    mutationFn: ({ formData, onUploadProgress }) =>
      knowledgeService.uploadDocument(agentId, formData, onUploadProgress),
    onSuccess: () => {
      queryClient.invalidateQueries(["knowledge", agentId]);
      toast.success("Document uploaded and processed successfully!", {
        duration: 4000,
      });
    },
    onError: (error) => {
      console.error("Upload error:", error);
      if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        toast.error(
          "Upload timeout - document processing is taking longer than expected. Large files may need more time or try uploading smaller documents.",
          { duration: 8000 }
        );
      } else if (error.response?.status === 413) {
        toast.error(
          "File too large - please choose a smaller file (max 10MB recommended)",
          {
            duration: 6000,
          }
        );
      } else if (error.response?.status === 400) {
        toast.error(
          error.response?.data?.message ||
            "Invalid file format - please use PDF, DOCX, or TXT files",
          { duration: 6000 }
        );
      } else if (error.response?.status >= 500) {
        toast.error(
          "Server processing error - please try again later or contact support",
          { duration: 6000 }
        );
      } else {
        toast.error(
          error.response?.data?.message ||
            "Failed to upload document - please try again",
          { duration: 5000 }
        );
      }
    },
  });

  // Delete knowledge mutation
  const deleteKnowledgeMutation = useMutation({
    mutationFn: (itemId) => knowledgeService.deleteKnowledge(agentId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries(["knowledge", agentId]);
      toast.success("Knowledge item deleted successfully!");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete knowledge item"
      );
    },
  });

  // Search knowledge mutation
  const searchKnowledgeMutation = useMutation({
    mutationFn: (data) => knowledgeService.searchKnowledge(agentId, data),
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to search knowledge"
      );
    },
  });

  const knowledgeItems = knowledgeData?.knowledge_items || [];
  const hasKnowledge = knowledgeItems.length > 0;

  return {
    knowledgeItems,
    knowledgeData,
    hasKnowledge,
    isLoadingKnowledge,
    knowledgeError,
    refetchKnowledge,
    addManualKnowledge: addManualKnowledgeMutation.mutateAsync,
    isAddingKnowledge: addManualKnowledgeMutation.isPending,
    updateManualKnowledge: updateManualKnowledgeMutation.mutateAsync,
    isUpdatingKnowledge: updateManualKnowledgeMutation.isPending,
    uploadDocument: uploadDocumentMutation.mutateAsync,
    isUploadingDocument: uploadDocumentMutation.isPending,
    deleteKnowledge: deleteKnowledgeMutation.mutateAsync,
    isDeletingKnowledge: deleteKnowledgeMutation.isPending,
    searchKnowledge: searchKnowledgeMutation.mutateAsync,
    isSearchingKnowledge: searchKnowledgeMutation.isPending,
  };
};
